'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@ifixit/ui/lib/utils';
import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';
import { Button } from './button';

function TooltipProvider({
   delayDuration = 0,
   ...props
}: ComponentProps<typeof TooltipPrimitive.Provider>) {
   return (
      <TooltipPrimitive.Provider
         data-slot="tooltip-provider"
         delayDuration={delayDuration}
         {...props}
      />
   );
}

function Tooltip({ ...props }: ComponentProps<typeof TooltipPrimitive.Root>) {
   return (
      <TooltipProvider>
         <TooltipPrimitive.Root data-slot="tooltip" {...props} />
      </TooltipProvider>
   );
}

function TooltipTrigger({ ...props }: ComponentProps<typeof TooltipPrimitive.Trigger>) {
   return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
   className,
   sideOffset = 0,
   children,
   ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
   return (
      <TooltipPrimitive.Portal>
         <TooltipPrimitive.Content
            data-slot="tooltip-content"
            sideOffset={sideOffset}
            className={cn(
               'bg-gray-950 text-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-tooltip w-fit origin-(--radix-tooltip-content-transform-origin) rounded-lg px-3 py-1.5 text-xs max-w-3xs md:max-w-2xs',
               className
            )}
            {...props}
         >
            {children}
            <TooltipPrimitive.Arrow className="bg-gray-950 fill-gray-950 z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
         </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
   );
}

interface TooltipButtonProps extends ComponentProps<'button'> {
   tooltip: ReactNode;
   tooltipProps?: Omit<ComponentProps<typeof TooltipContent>, 'children'>;
}

function TooltipButton({
   tooltip,
   tooltipProps,
   className,
   onClick,
   children,
   ...buttonProps
}: TooltipButtonProps) {
   const [open, setOpen] = useState(false);

   return (
      <Tooltip open={open} onOpenChange={setOpen}>
         <TooltipTrigger asChild>
            <Button
               className={cn(className, open && 'bg-accent')}
               onClick={e => {
                  setOpen(!open);
                  onClick?.(e);
               }}
               {...buttonProps}
            >
               {children}
            </Button>
         </TooltipTrigger>
         <TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>
      </Tooltip>
   );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipButton };

export type { TooltipButtonProps };
